import { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Text, Loader, Center, Stack } from '@mantine/core';
import { StickyNote, Sparkles } from 'lucide-react';
import { useNotes, useUpdateNote } from '../../hooks/useNotes';
import { useUser } from '../../contexts/UserContext';
import { Note } from '../../types/notes';
import { NoteCard } from './NoteCard';
import { Minimap } from './Minimap';

interface NotesGridProps {
  searchQuery?: string;
  onNoteDelete?: (id: number) => void;
  layoutMode: 'organized' | 'free';
  focusNoteId?: number | null;
  onNoteFocused?: () => void;
}

// Native Draggable Note Component - smooth and reliable
function DraggableNote({ 
  note, 
  onNoteDelete, 
  onPositionUpdate,
  isDragging,
  onDragStart,
  onDragEnd,
  isTopNote,
  onBringToFront,
  shouldFocus,
  onFocused
}: { 
  note: Note; 
  onNoteDelete?: (id: number) => void;
  onPositionUpdate: (id: number, x: number, y: number) => void;
  isDragging: boolean;
  onDragStart: (note: Note) => void;
  onDragEnd: () => void;
  isTopNote: boolean;
  onBringToFront: (noteId: number) => void;
  shouldFocus?: boolean;
  onFocused?: () => void;
}) {
  const [position, setPosition] = useState({ 
    x: note.positionX || 50, 
    y: note.positionY || 50 
  });
  const noteRef = useRef<HTMLDivElement>(null);

  // Update position when note changes from backend (but not during drag)
  useEffect(() => {
    if (!isDragging) {
      setPosition({ 
        x: note.positionX || 50, 
        y: note.positionY || 50 
      });
    }
  }, [note.positionX, note.positionY, isDragging]);


  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    
    // Calculate initial mouse offset from the note's current position
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startNoteX = position.x;
    const startNoteY = position.y;
    
    // Track final position during drag
    let finalPosition = { x: startNoteX, y: startNoteY };
    let autoScrollInterval: NodeJS.Timeout | null = null;
    
    onDragStart(note);

    const getScrollContainer = () => {
      return document.querySelector('.free-layout-container') as HTMLElement;
    };

    // Store initial scroll position
    const container = getScrollContainer();
    if (container) {
      container.dataset.initialScrollX = container.scrollLeft.toString();
      container.dataset.initialScrollY = container.scrollTop.toString();
    }

    const autoScroll = (mouseX: number, mouseY: number) => {
      const container = getScrollContainer();
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const scrollSpeed = 8; // Moderate scroll speed
      const threshold = 50; // Distance from edge to trigger scroll

      let scrollX = 0;
      let scrollY = 0;

      // Check horizontal edges
      if (mouseX < rect.left + threshold) {
        scrollX = -scrollSpeed;
      } else if (mouseX > rect.right - threshold) {
        scrollX = scrollSpeed;
      }

      // Check vertical edges
      if (mouseY < rect.top + threshold) {
        scrollY = -scrollSpeed;
      } else if (mouseY > rect.bottom - threshold) {
        scrollY = scrollSpeed;
      }

      // Apply scroll
      if (scrollX !== 0 || scrollY !== 0) {
        container.scrollBy(scrollX, scrollY);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const container = getScrollContainer();
      
      // Get current scroll position
      const currentScrollX = container?.scrollLeft || 0;
      const currentScrollY = container?.scrollTop || 0;
      
      // Calculate how much the mouse has moved from start
      const deltaX = e.clientX - startMouseX;
      const deltaY = e.clientY - startMouseY;
      
      // Apply the delta to the note's starting position, accounting for scroll changes
      const newX = Math.max(0, startNoteX + deltaX + (currentScrollX - (container?.dataset.initialScrollX ? parseInt(container.dataset.initialScrollX) : 0)));
      const newY = Math.max(0, startNoteY + deltaY + (currentScrollY - (container?.dataset.initialScrollY ? parseInt(container.dataset.initialScrollY) : 0)));
      
      finalPosition = { x: newX, y: newY };
      setPosition(finalPosition);

      // Auto-scroll when near edges
      autoScroll(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      if (autoScrollInterval) {
        clearInterval(autoScrollInterval);
      }
      
      onDragEnd();
      // Use the final position from the drag
      onPositionUpdate(note.id, finalPosition.x, finalPosition.y);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  }, [note, position, onDragStart, onDragEnd, onPositionUpdate]);

  return (
    <Box
      ref={noteRef}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        zIndex: isDragging ? 1000 : (isTopNote ? 100 : 1),
        transition: isDragging ? 'none' : 'transform 0.2s ease',
        transform: isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
        opacity: isDragging ? 0.9 : 1,
      }}
      onClick={() => onBringToFront(note.id)}
    >
      <NoteCard
        note={note}
        onDelete={onNoteDelete}
        style={{
          width: note.width || 250,
          minHeight: note.height || 180,
          pointerEvents: isDragging ? 'none' : 'auto',
        }}
        showControls={true}
        isDraggable={true}
        onMouseDown={handleMouseDown}
        shouldFocus={shouldFocus}
        onFocused={onFocused}
      />

    </Box>
  );
}

export const NotesGrid = ({ searchQuery, onNoteDelete, layoutMode, focusNoteId, onNoteFocused }: NotesGridProps) => {
  const { currentUser } = useUser();
  const { data: notes, isLoading, error } = useNotes(currentUser?.email || '');
  const updateNoteMutation = useUpdateNote();
  // Demo seeding removed
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [draggedNoteId, setDraggedNoteId] = useState<number | null>(null);
  const [topNoteId, setTopNoteId] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize for responsive grid
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter notes based on search query
  const filteredNotes = notes?.filter(note => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      note.content.toLowerCase().includes(query) ||
      (note.title && note.title.toLowerCase().includes(query)) ||
      note.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }) || [];

  // Bring note to front when touched/interacted with (only in free mode)
  const bringToFront = useCallback((noteId: number) => {
    if (layoutMode === 'free') {
      setTopNoteId(noteId);
    }
  }, [layoutMode]);


  // Navigate to specific coordinates
  const navigateToPosition = useCallback((x: number, y: number) => {
    if (!gridRef.current) return;

    const container = gridRef.current;
    const viewportWidth = container.clientWidth;
    const viewportHeight = container.clientHeight;

    // Center the viewport on the target position
    container.scrollTo({
      left: x - viewportWidth / 2,
      top: y - viewportHeight / 2,
      behavior: 'smooth'
    });
  }, []);

  // Handle drag events for free mode - simple and clean
  const handleDragStart = useCallback((note: Note) => {
    setDraggedNoteId(note.id);
    bringToFront(note.id); // Bring dragged note to front
  }, [bringToFront]);

  const handleDragEnd = useCallback(() => {
    setDraggedNoteId(null);
  }, []);

  const handlePositionUpdate = useCallback((id: number, x: number, y: number) => {
    updateNoteMutation.mutate({
      id,
      data: {
        positionX: Math.max(0, x),
        positionY: Math.max(0, y)
      }
    });
  }, [updateNoteMutation]);


  if (isLoading) {
    return (
      <Center style={{ minHeight: '400px' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" color="var(--app-text-secondary)" />
          <Text size="md" color="var(--app-text-secondary)">
            Loading your notes...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center style={{ minHeight: '400px' }}>
        <Stack align="center" gap="md">
          <StickyNote size={48} color="var(--app-text-muted)" />
          <Text size="md" color="var(--app-text-secondary)">
            Failed to load notes. Please try again.
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!filteredNotes || filteredNotes.length === 0) {
    return (
      <Center style={{ minHeight: '400px' }}>
        <Stack align="center" gap="lg" style={{ maxWidth: '400px', textAlign: 'center' }}>
          {searchQuery ? (
            <>
              <StickyNote size={64} color="var(--app-text-muted)" />
              <div>
                <Text size="lg" fw={600} color="var(--app-text-primary)" mb="xs">
                  No notes found
                </Text>
                <Text size="md" color="var(--app-text-secondary)">
                  No notes match "{searchQuery}". Try a different search term.
                </Text>
              </div>
            </>
          ) : (
            <>
              <Box style={{ position: 'relative' }}>
                <StickyNote size={64} color="var(--app-text-muted)" />
                <Sparkles 
                  size={24} 
                  color="#FFE066" 
                  style={{ 
                    position: 'absolute', 
                    top: -8, 
                    right: -8,
                    animation: 'sparkle 2s ease-in-out infinite'
                  }} 
                />
              </Box>
              <div>
                <Text size="xl" fw={700} color="var(--app-text-primary)" mb="xs">
                  Create your first note!
                </Text>
                <Text size="md" color="var(--app-text-secondary)" mb="md">
                 Capture ideas, thoughts, and important information
                </Text>
                {/* Demo button removed */}
                <Text size="sm" color="var(--app-text-muted)" style={{ marginTop: 8 }}>
                  💡 Or click the "Add Note" button above to get started
                </Text>
              </div>
            </>
          )}
        </Stack>
      </Center>
    );
  }

  // Render organized grid layout
  const renderOrganizedLayout = () => {
    const getGridLayout = () => {
      if (windowWidth <= 768) {
        // Mobile: Single column
        return { columns: 1, gap: 16, padding: 16 };
      } else {
        // Desktop: Dynamic columns based on screen width (minimum 2 columns)
        return { columns: Math.max(2, Math.floor((windowWidth - 100) / 280)), gap: 24, padding: 32 };
      }
    };

    const layout = getGridLayout();

    return (
      <Box
        ref={gridRef}
        className="organized-layout-container"
        style={{
          position: 'relative',
          height: '100%',
          overflow: 'auto',
          padding: `${layout.padding}px`,
          // Custom scrollbar styling for subtle appearance
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
        }}
      >
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layout.columns}, 1fr)`,
            gap: `${layout.gap}px`,
            alignItems: 'start'
          }}
        >
          {filteredNotes.map((note, index) => (
            <Box
              key={note.id}
              style={{
                animation: `slideInUp 0.3s ease-out ${index * 0.05}s both`,
                transform: `rotate(${(index % 5 - 2) * 0.5}deg)`,
                transition: 'transform 0.2s ease',
                zIndex: 1, // Static z-index for organized mode
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = `rotate(0deg) translateY(-2px)`;
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = `rotate(${(index % 5 - 2) * 0.5}deg) translateY(0)`;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <NoteCard
                note={note}
                onDelete={onNoteDelete}
                style={{
                  width: '100%',
                  height: 'auto',
                  minHeight: '180px'
                }}
                showControls={true} // Show controls in organized mode
                shouldFocus={focusNoteId === note.id}
                onFocused={onNoteFocused}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Calculate scrollable container size based on note positions
  const getScrollableContainerSize = () => {
    if (!filteredNotes || filteredNotes.length === 0) {
      return {
        width: '3000px',
        height: '3000px'
      };
    }

    // Find the bounds of all notes
    let minX = 0, minY = 0, maxX = window.innerWidth, maxY = window.innerHeight;
    
    filteredNotes.forEach(note => {
      const x = note.positionX || 0;
      const y = note.positionY || 0;
      const right = x + (note.width || 250);
      const bottom = y + (note.height || 180);
      
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    // Add generous padding and ensure minimum size
    const padding = 500;
    const width = Math.max(3000, maxX - minX + padding * 2);
    const height = Math.max(3000, maxY - minY + padding * 2);

    return {
      width: width + 'px',
      height: height + 'px'
    };
  };

  const containerSize = getScrollableContainerSize();

  // Render free positioning layout
  const renderFreeLayout = () => {
    return (
      <Box
        ref={gridRef}
        className="free-layout-container"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflowX: 'auto', // Show horizontal scrollbar when needed
          overflowY: 'auto', // Show vertical scrollbar when needed  
          // Custom scrollbar styling for subtle appearance
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Large scrollable container */}
        <Box
          style={{
            position: 'relative',
            width: containerSize.width,
            height: containerSize.height,
            padding: '20px',
          }}
        >
          
          {filteredNotes.map((note) => (
            <DraggableNote
              key={note.id}
              note={note}
              onNoteDelete={onNoteDelete}
              onPositionUpdate={handlePositionUpdate}
              isDragging={draggedNoteId === note.id}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              isTopNote={topNoteId === note.id}
              onBringToFront={bringToFront}
              shouldFocus={focusNoteId === note.id}
              onFocused={onNoteFocused}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Main render
  return (
    <>
      {layoutMode === 'free' ? renderFreeLayout() : renderOrganizedLayout()}
      
      {/* Minimap for free mode */}
      {layoutMode === 'free' && (
        <Minimap
          notes={filteredNotes}
          containerRef={gridRef}
          onNavigateToNote={navigateToPosition}
          isVisible={true}
        />
      )}
      
      
      <style>
        {`
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px) rotate(${Math.random() * 6 - 3}deg);
            }
            to {
              opacity: 1;
              transform: translateY(0) rotate(0deg);
            }
          }

          @keyframes sparkle {
            0%, 100% { 
              opacity: 0.8; 
              transform: scale(1) rotate(0deg); 
            }
            50% { 
              opacity: 1; 
              transform: scale(1.2) rotate(180deg); 
            }
          }

          /* Subtle scrollbar styling for both layout containers */
          .free-layout-container::-webkit-scrollbar,
          .organized-layout-container::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          .free-layout-container::-webkit-scrollbar-track,
          .organized-layout-container::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
          }
          
          .free-layout-container::-webkit-scrollbar-thumb,
          .organized-layout-container::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 4px;
          }
          
          .free-layout-container::-webkit-scrollbar-thumb:hover,
          .organized-layout-container::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.5);
          }
          
          .free-layout-container::-webkit-scrollbar-corner,
          .organized-layout-container::-webkit-scrollbar-corner {
            background: transparent;
          }

          @media (max-width: 768px) {
            .notes-grid {
              padding: 16px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default NotesGrid;